import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams, Link } from "react-router-dom"
import { FaAngleLeft, FaCheckCircle } from 'react-icons/fa'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import { apiURL, filesURL } from 'config/globals'
import Topbar from 'components/Topbar'
import FormRow from 'components/FormRow'
import SaveButton from 'components/SaveButton'
import { checkAuth } from 'utils/Auth'
import ReactImagePickerEditor from 'react-image-picker-editor'
import 'react-image-picker-editor/dist/index.css'
import moment from "moment"
import { Modal } from "react-bootstrap"
import StickyFooter from "components/StickyFooter"

const BuildingNew = () => {
    const [currentUser, setCurrentUser] = useState()
    const [museum, setMuseum] = useState({})
    const [form, setForm] = useState({
        modelType: '3d'
    })
    const [buildingId, setBuildingId] = useState('')
    const [isSaving, setIsSaving] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [successMessage, setSuccessMessage] = useState('')
    
    const navigate = useNavigate()
    const { entityId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
        }
        start()
    }, [])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    const handleChange = (field, value) => {
        setForm({
            ...form,
            [field]: value
        })
    }

    const save = async (event) => {
        event.preventDefault()
        event.stopPropagation()

        setIsSaving(true)

        const submittedForm = {...form}
        submittedForm.museumId = entityId
        submittedForm.createdBy = currentUser.email
        
        try {
            const response = await axios.post(`${apiURL}/create-building`, submittedForm)
            
            if (!response.data.success) {
                setErrorMessage(response.data.error || response.data)
                setIsSaving(false)
                return
            }

            setBuildingId(response.data.id)
            setSuccessMessage("The building data has been successfully saved.")
        } catch (error) {
            setErrorMessage(JSON.stringify(error))
        }
        setIsSaving(false)
    }

    const navigateToBuildingView = () => {
        navigate(`/museum/${entityId}/documentation/buildings/${buildingId}/view`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width" style={{paddingBottom: 300, height: 'fit-content'}}>
                {museum.id &&
                    <>
                        <div>
                            <Link to={`/museum/${museum.id}/documentation/buildings`} className="no-decoration">
                                <FaAngleLeft /> Back to Building Documentation
                            </Link>
                        </div>
                        <header className="mt-2">
                            <div className="float-start">
                                <h1>New Building</h1>
                            </div>
                        </header>
                        <h3 className="mb-2">Archive information</h3>
                        <p className="mb-4">
                            Overall information about the building. Usually, information that does not change along the time. 
                        </p>
                        <FormRow
                            key="buildingName"
                            label="Name"
                            required={true}
                            content={
                                <Form.Control
                                    name="name"
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="buildingDescription"
                            label="Description"
                            required={true}
                            content={
                                <Form.Control
                                    name="description"
                                    as="textarea"
                                    rows={10}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="inventoryNumber"
                            label="Inventory Number"
                            required={true}
                            content={
                                <Form.Control
                                    name="inventoryNumber"
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="mainImage"
                            label="Main Image"
                            hint={<>Accepted types: JPG or PNG<br/>Recommended size: 1200x800</>}
                            required={true}
                            content={
                                <div className="mb-3">
                                    <ReactImagePickerEditor
                                        config={{
                                            borderRadius: '8px',
                                            language: 'en',
                                            width: '330px',
                                            height: '250px',
                                            objectFit: 'contain',
                                            compressInitial: null,
                                        }}
                                        imageChanged={(dataUri) => {
                                            handleChange('mainImage', {
                                                uri: dataUri
                                            })
                                        }}
                                    />
                                </div>
                            }
                        />
                        <FormRow
                            key="constructionDate"
                            label="Construction date"
                            content={
                                <Form.Control
                                    name="constructionDate"
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="buildingPurpose"
                            label="Purpose of building"
                            content={
                                <Form.Control
                                    name="buildingPurpose"
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <h5 className="my-4 mb-2">Excavation</h5>
                        <FormRow
                            key="excavationLocation"
                            label="Location"
                            content={
                                <Form.Control
                                    name="excavationLocation"
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="excavationDate"
                            label="Date"
                            content={
                                <Form.Control
                                    name="excavationDate"
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="settlementPhase"
                            label="Settlement phase"
                            content={
                                <Form.Control
                                    name="settlementPhase"
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <h5 className="my-4 mb-2">Excavation responsible</h5>
                        <FormRow
                            key="excavationCompany"
                            label="Company"
                            content={
                                <Form.Control
                                    name="excavationCompany"
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="excavationContactPerson"
                            label="Contact person"
                            content={
                                <Form.Control
                                    name="excavationContactPerson"
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <FormRow
                            key="excavationContactDetails"
                            label="Contact details"
                            content={
                                <Form.Control
                                    name="excavationContactDetails"
                                    as="textarea"
                                    rows={5}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <h5 className="my-4 mb-2">Archaeological sources</h5>
                        <FormRow
                            key="archaeologicalSources"
                            label="Sources"
                            content={
                                <Form.Control
                                    name="archaeologicalSources"
                                    as="textarea"
                                    rows={5}
                                    onChange={e => handleChange(e.target.name, e.target.value)}
                                />
                            }
                        />
                        <h3 className="my-4 mb-2">Documentation model <span className="text-danger">*</span></h3>
                        <p className="mb-4">
                            This model is used for adding documentation records to this building. Please choose a 3D model or an image.
                        </p>
                        <FormRow
                            key="3dModel"
                            label={
                                <span className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="modelType"
                                        id="modelType3d"
                                        checked={form.modelType === '3d'}
                                        onChange={e => handleChange('modelType', '3d')}
                                    />
                                    <label className="form-check-label" htmlFor="modelType3d">
                                        3D Model <small className="fw-light">(GLB)</small>
                                    </label>
                                </span>
                            }
                            content={
                                <div className="mt-1 mb-3">
                                    <input
                                        type="file"
                                        disabled={form.modelType !== '3d'}
                                        onChange={(event) => {
                                            const file = event.target.files[0]
                                            const reader = new FileReader();
                                            reader.onloadend = () => {
                                                handleChange('modelFile', {
                                                    uri: reader.result,
                                                    name: file.name
                                                })
                                            };
                                            reader.readAsDataURL(file)
                                        }}
                                    />
                                </div>
                            }
                        />
                        <FormRow
                            key="2dModel"
                            label={
                                <span className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="modelType"
                                        id="modelType2d"
                                        checked={form.modelType === '2d'}
                                        onChange={e => handleChange('modelType', '2d')}
                                    />
                                    <label className="form-check-label" htmlFor="modelType2d">
                                        Image <small className="fw-light">(JPG or PNG)</small>
                                    </label>
                                </span>
                            }
                            content={
                                <div className={`mt-1 mb-3 ${form.modelType !== '2d' ? 'disabled' : ''}`}>
                                    <ReactImagePickerEditor
                                        config={{
                                            borderRadius: '8px',
                                            language: 'en',
                                            width: '330px',
                                            height: '250px',
                                            objectFit: 'contain',
                                            compressInitial: null,
                                        }}
                                        imageChanged={(dataUri) => {
                                            handleChange('modelFile', {
                                                uri: dataUri
                                            })
                                        }}
                                    />
                                </div>
                            }
                        />
                    </>
                }
                {loadError &&
                    <Alert key='loadError' variant='danger'>
                        {loadError}
                    </Alert>
                }
                {errorMessage &&
                    <Modal show={!!errorMessage} onHide={() => setErrorMessage("")} backdrop="static" centered>
                        <Modal.Header closeButton>
                            <Modal.Title className="text-danger">
                                Error
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {errorMessage}
                        </Modal.Body>
                    </Modal>
                }
                {successMessage &&
                    <Modal show={!!successMessage} onHide={() => setSuccessMessage("")} backdrop="static" centered>
                        <Modal.Header>
                            <Modal.Title className="text-success">
                                <FaCheckCircle /> Success
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {successMessage}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="primary" onClick={navigateToBuildingView}>
                                View building
                            </Button>
                        </Modal.Footer>
                    </Modal>
                }
                <StickyFooter>
                    <SaveButton isSubmitting={isSaving} onClick={save} title="Save" />
                </StickyFooter>
            </main>
        </>
    )
}

export default BuildingNew